import { HeadingLevel } from '@/components';
import { HeadingTag } from '@/globals/HeadingTag';
import { Component, Host, Prop, h, State } from '@stencil/core';
import { bankid } from 'pn-design-assets/pn-assets/icons.js';
import { DeviceType } from './types';
import { PnLevelUpService } from './pn-level-up.service';
import { LocalStorageService } from '@/globals/LocalStorageService';

@Component({
  tag: 'pn-level-up',
  styleUrl: 'pn-level-up.scss'
})
export class PnLevelUp {
  @Prop() heading?: string;
  @Prop() headingLevel: HeadingLevel = 'h2';
  @Prop() preamble?: string;
  @Prop() imageUrl?: string;
  @Prop() imageAltText?: string;
  @Prop() endpoint: string;
  @Prop() webModalContinueOtherDeviceLabel: string;
  @Prop() webModalContinueThisDeviceLabel: string;
  @Prop() mobileModalContinueThisDeviceLabel: string;
  @Prop() mobileModalContinueOtherDeviceLabel: string;
  @Prop() modalInstructionPhoneLineOne: string;
  @Prop() modalInstructionPhoneLineTwo: string;
  @Prop() modalInstructionPhoneLineThree: string;
  @Prop() modalHeadingText: string;
  @Prop() modalOutdatedQRMessage: string;
  @Prop() modalExpiresInMessage: string;
  @Prop() qrAriaLabel: string;
  @Prop() leveledupMessage: string;
  @Prop() isLeveledUp: boolean = false;

  @State() private isLoading: boolean = false;
  @State() private isRecentLeveledUp: boolean = false;
  @State() private errorMessage: string;
  private showModal: boolean = false;

  private readonly _android: DeviceType = 'android';
  private readonly _ios: DeviceType = 'ios';
  private readonly _web: DeviceType = 'web';
  private readonly levelupLocalStorageKey = 'levelup';

  private _system: DeviceType;
  private _levelupService: PnLevelUpService;
  private _storage: LocalStorageService = new LocalStorageService();
  private modal: HTMLElement;

  componentWillLoad() {
    if (!this.isLeveledUp) {
      this.checkRecentLevelUp();
    }

    this._levelupService = new PnLevelUpService(this.endpoint);
    this._system = this.getDeviceType();
  }

  getDeviceType(): DeviceType {
    const userAgent = navigator.userAgent;
    
    if (/android/i.test(userAgent)) {
      return this._android;
    }
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return this._ios;
    }
    
    return this._web;
  }

  private openLocalApp = async () => {
    this.isLoading = true;
    let authResponse = await this._levelupService.levelupAuthorize(this._system, null)
    window.location.href = authResponse.url;
    this.isLoading = false;
    await this._levelupService.levelupTokenGrant(authResponse.id, '30', null).then((response) => {
      if (response.code === 200) {
        this._levelupService.newLevelupUser();
        this.isRecentLeveledUp = true;
        this.errorMessage = null;
      }
      else {
        this.errorMessage = response.customMessage;
      }
    });
  }

  private openModal() {
      if (this.showModal) {
        this.modal.setAttribute('open', 'false');
        this.showModal = false;
      } 
        this.modal.setAttribute('open', 'true');
        this.showModal = true;
  }

  private renderWebButtonOptions() {
    return ( 
      <div class="cta" role="group">
        {this.webModalContinueOtherDeviceLabel &&
        <pn-button
          icon={bankid}
          left-icon='true'
          appearance='default'
          label={this.webModalContinueOtherDeviceLabel}
          onClick={() => this.openModal()}>
        </pn-button>}
        {this.webModalContinueThisDeviceLabel &&
        <pn-button
          icon={bankid}
          left-icon='true'
          appearance='light'
          variant='outlined'
          label={this.webModalContinueThisDeviceLabel}
          onClick={() => this.openLocalApp()}
          loading={this.isLoading}>
        </pn-button>}
      </div>);
  }

  private renderMobileButtonOptions() {
    return (
      <div class="cta" role="group">
        {this.mobileModalContinueThisDeviceLabel &&
        <pn-button
          icon={bankid}
          left-icon='true'
          appearance='default'
          label={this.mobileModalContinueThisDeviceLabel}
          onClick={() => this.openLocalApp()}
          loading={this.isLoading}>
        </pn-button>}
        {this.mobileModalContinueOtherDeviceLabel &&
        <pn-button
          icon={bankid}
          left-icon='true'
          appearance='light'
          variant='outlined'
          label={this.mobileModalContinueOtherDeviceLabel}
          onClick={() => this.openModal()}>
        </pn-button>}
      </div>);
  }

  private presentResult() {
    if (this.isLeveledUp) {
      return null;
    }

    return (<pn-toast text={this.leveledupMessage} appearance='success'></pn-toast>);
  }

  private checkRecentLevelUp() {
    let levelupStorage = this._storage.get(this.levelupLocalStorageKey);
    if (levelupStorage === null) {
      this.isRecentLeveledUp = false;
    }
    else if (levelupStorage.expire > Date.now()) {
      this.isRecentLeveledUp = true;
    }
    else {
      this._storage.remove(this.levelupLocalStorageKey);
      this.isRecentLeveledUp = false;
    }
  }

  private get hasButtonTexts(): boolean {
    if (this._system === this._web) {
      return Boolean(this.webModalContinueOtherDeviceLabel) && Boolean(this.webModalContinueThisDeviceLabel);
    }
    else {
      return Boolean(this.mobileModalContinueOtherDeviceLabel) && Boolean(this.mobileModalContinueThisDeviceLabel);
    }
  }

  render() {
    const headingId = 'pn-level-up-heading';
    return (
      <Host>
        {this.isLeveledUp || this.isRecentLeveledUp 
        ? (this.presentResult()) 
        : (<div class="pn-level-up" role="region" aria-labelledby={this.heading ? headingId : undefined}>
            <div class="content">
              {this.heading && (
                <HeadingTag id={headingId} level={this.headingLevel} cssClass="heading">
                  {this.heading}
                </HeadingTag>
              )}
              {this.preamble && (
                <div class="preamble">
                  {this.preamble}
                </div>
              )}
              {this.hasButtonTexts && this._system === this._web && this.renderWebButtonOptions()}
              {this.hasButtonTexts && this._system !== this._web && this.renderMobileButtonOptions()}
            </div>
            <div class="image-container" aria-hidden={!this.imageAltText}>
              {this.imageUrl && <img class="image graphics" src={this.imageUrl} alt={this.imageAltText || ''} loading="lazy" />}
            </div>
          </div>
        )}
        {this.errorMessage && <pn-toast text={this.errorMessage} appearance='warning'></pn-toast>}
        <pn-level-up-modal
          ref={el => this.modal = el as HTMLElement}
          open={false}
          endpoint={this.endpoint}
          instruction-phone-line-one={this.modalInstructionPhoneLineOne}
          instruction-phone-line-two={this.modalInstructionPhoneLineTwo}
          instruction-phone-line-three={this.modalInstructionPhoneLineThree}
          heading-text={this.modalHeadingText}
          outdated-q-r-message={this.modalOutdatedQRMessage}
          expires-in-message={this.modalExpiresInMessage}
          qr-aria-label={this.qrAriaLabel}
          system={this._android}>
        </pn-level-up-modal>
      </Host>
    );
  }
}

